/**
 * Форматирование чисел и площадей.
 *
 * Единственное правило, из-за которого этот модуль вообще существует:
 * **`null` — это «нет данных», а не ноль**. Функции возвращают признак
 * `missing`, а компонент показывает явную пометку. Нигде нет `value ?? 0`.
 */

import { defineMessages } from "../i18n/define";
import { numberLocale, runtimeLocale, tr } from "../i18n/runtime";
import { dateLong } from "../astra/lib/format";

export { plural, countOf } from "../astra/lib/format";

const M = defineMessages({
  missing: {
    id: "common.vocab.noData",
    defaultMessage: "нет данных",
    description: "Подпись на месте числа или даты, которых нет: это «данных нет», а не ноль; строчная, встаёт в таблицы и плашки",
  },
});

const formats = new Map<string, Intl.NumberFormat>();
function nf(min: number, max: number): Intl.NumberFormat {
  const locale = numberLocale();
  const key = `${locale}|${min}|${max}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, { minimumFractionDigits: min, maximumFractionDigits: max });
    formats.set(key, format);
  }
  return format;
}

/** Подпись «нет данных» на языке интерфейса — вызывать при рендере, не на уровне модуля. */
export function missingLabel(): string {
  return tr(M.missing);
}

/**
 * Прежняя константа. Заморожена на русском: код зовёт `missingLabel()`, константа
 * осталась только как ожидаемое значение в тестах на языке-источнике.
 */
// i18n-ignore: устаревшая константа, замена — missingLabel()
export const MISSING_LABEL = "нет данных";

export interface Formatted {
  text: string;
  missing: boolean;
}

/** Микрогектары → гектары. `null` остаётся «нет данных». */
export function formatArea(
  microha: number | null | undefined,
  options: { rounded?: boolean } = {},
): Formatted {
  if (microha === null || microha === undefined) {
    return { text: missingLabel(), missing: true };
  }
  // Сводные плашки округляются до сотых (длинное число там переносится);
  // точное значение остаётся в подсказке и таблицах ниже.
  const formatter = options.rounded ? nf(2, 2) : nf(2, 6);
  return { text: formatter.format(microha / 1_000_000), missing: false };
}

export function formatCount(value: number | null | undefined): Formatted {
  if (value === null || value === undefined) {
    return { text: missingLabel(), missing: true };
  }
  return { text: nf(0, 3).format(value), missing: false };
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return missingLabel();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  // Русский текст не меняется ни на запятую («26 августа 2026 г.»): его читают
  // тесты и привычка; другие языки берут дату из каталога месяцев.
  if (runtimeLocale() === "ru") {
    return parsed.toLocaleDateString("ru-RU", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  const long = dateLong(value);
  return long === "—" ? value : long;
}
